/**
 * Test-signal generator (PLAN.md §15.1). Pure TS: used by the app (Analysis → New →
 * Generate, Live "Test generator") and by scripts/make-fixtures.ts.
 *
 * Level convention: for deterministic signals `level` is the PEAK amplitude in dBFS
 * (a 0 dBFS sine has peak 1). For noise it is the RMS level of a sine with the same
 * power, i.e. RMS = 10^(level/20)/√2, so "−20 dBFS noise" has the power of a −20 dBFS sine.
 */
import { Rng } from './random';

export const SIGNAL_TYPES = [
  'sine',
  'multitone',
  'square',
  'sawtooth',
  'impulse',
  'impulse-train',
  'click-train',
  'white-noise',
  'pink-noise',
  'sweep-lin',
  'sweep-log',
] as const;
export type SignalType = (typeof SIGNAL_TYPES)[number];

export const SIGNAL_LABELS: Record<SignalType, string> = {
  sine: 'Sine',
  multitone: 'Multi-tone',
  square: 'Square',
  sawtooth: 'Sawtooth',
  impulse: 'Impulse (single)',
  'impulse-train': 'Impulse train',
  'click-train': 'Click train',
  'white-noise': 'White noise',
  'pink-noise': 'Pink noise',
  'sweep-lin': 'Linear sweep',
  'sweep-log': 'Logarithmic sweep',
};

export interface Tone {
  frequency: number;
  level: number;
}

export interface GeneratorParams {
  type: SignalType;
  sampleRate: number;
  /** seconds */
  duration: number;
  /** dBFS (see level convention above) */
  level: number;
  /** Hz: sine/square/sawtooth/impulse-train */
  frequency?: number;
  /** degrees, sine only */
  phase?: number;
  /** multitone */
  tones?: Tone[];
  /** sweeps: start/end frequency in Hz */
  f0?: number;
  f1?: number;
  /** click train interval in seconds */
  interval?: number;
  /** single impulse position in seconds */
  at?: number;
  /** square/sawtooth: reduce aliasing with PolyBLEP (default false = naive, aliased) */
  bandLimited?: boolean;
  /** noise seed */
  seed?: number;
}

export interface GeneratedSignal {
  name: string;
  sampleRate: number;
  channels: Float32Array[];
}

const amp = (db: number) => Math.pow(10, db / 20);
const frac = (x: number) => x - Math.floor(x);
const fmtHz = (f: number) => (f >= 1000 ? `${+(f / 1000).toFixed(3)} kHz` : `${+f.toFixed(2)} Hz`);
const fmtDb = (d: number) => `${d < 0 ? '−' : ''}${Math.abs(+d.toFixed(1))} dBFS`;

export function signalName(p: GeneratorParams): string {
  const L = fmtDb(p.level);
  switch (p.type) {
    case 'sine':
    case 'square':
    case 'sawtooth':
    case 'impulse-train':
      return `${SIGNAL_LABELS[p.type]} ${fmtHz(p.frequency ?? 1000)} ${L}`;
    case 'multitone':
      return `Multi-tone ${(p.tones ?? []).map((t) => fmtHz(t.frequency)).join(' + ')}`;
    case 'sweep-lin':
    case 'sweep-log':
      return `${SIGNAL_LABELS[p.type]} ${fmtHz(p.f0 ?? 20)}–${fmtHz(p.f1 ?? 20000)} ${L}`;
    case 'click-train':
      return `Click train every ${p.interval ?? 1} s ${L}`;
    default:
      return `${SIGNAL_LABELS[p.type]} ${L}`;
  }
}

function polyBlep(t: number, dt: number): number {
  if (t < dt) {
    const u = t / dt;
    return u + u - u * u - 1;
  }
  if (t > 1 - dt) {
    const u = (t - 1) / dt;
    return u * u + u + u + 1;
  }
  return 0;
}

function addSine(x: Float32Array, fs: number, f: number, a: number, phaseCycles = 0): void {
  const c = f / fs;
  for (let n = 0; n < x.length; n++) {
    x[n]! += a * Math.sin(2 * Math.PI * frac(c * n + phaseCycles));
  }
}

function normaliseRms(x: Float32Array, targetRms: number): void {
  let s = 0;
  for (let i = 0; i < x.length; i++) s += x[i]! * x[i]!;
  const r = Math.sqrt(s / Math.max(1, x.length));
  const g = r > 0 ? targetRms / r : 0;
  for (let i = 0; i < x.length; i++) x[i] = Math.max(-1, Math.min(1, x[i]! * g));
}

export function generate(p: GeneratorParams): GeneratedSignal {
  const fs = p.sampleRate;
  const n = Math.max(0, Math.round(p.duration * fs));
  const x = new Float32Array(n);
  const A = amp(p.level);
  const f = p.frequency ?? 1000;

  switch (p.type) {
    case 'sine':
      addSine(x, fs, f, A, (p.phase ?? 0) / 360);
      break;
    case 'multitone':
      for (const t of p.tones ?? []) addSine(x, fs, t.frequency, amp(t.level));
      break;
    case 'square':
    case 'sawtooth': {
      const dt = f / fs;
      for (let i = 0; i < n; i++) {
        const t = frac(dt * i);
        let v: number;
        if (p.type === 'sawtooth') {
          v = 2 * t - 1;
          if (p.bandLimited) v -= polyBlep(t, dt);
        } else {
          v = t < 0.5 ? 1 : -1;
          if (p.bandLimited) v += polyBlep(t, dt) - polyBlep(frac(t + 0.5), dt);
        }
        x[i] = A * v;
      }
      break;
    }
    case 'impulse': {
      const i = Math.round((p.at ?? 0.1) * fs);
      if (i >= 0 && i < n) x[i] = A;
      break;
    }
    case 'impulse-train':
    case 'click-train': {
      const period = p.type === 'impulse-train' ? fs / f : (p.interval ?? 1) * fs;
      for (let k = 0; ; k++) {
        const i = Math.round(k * period);
        if (i >= n) break;
        x[i] = A;
      }
      break;
    }
    case 'white-noise': {
      const r = new Rng(p.seed ?? 1);
      for (let i = 0; i < n; i++) x[i] = r.gauss();
      normaliseRms(x, A / Math.SQRT2);
      break;
    }
    case 'pink-noise': {
      // Paul Kellet's refined pink filter (−3 dB/octave within ±0.05 dB above ~10 Hz at 44.1k).
      const r = new Rng(p.seed ?? 1);
      let b0 = 0,
        b1 = 0,
        b2 = 0,
        b3 = 0,
        b4 = 0,
        b5 = 0,
        b6 = 0;
      for (let i = 0; i < n; i++) {
        const w = r.gauss();
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856;
        b4 = 0.55 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.016898;
        x[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
        b6 = w * 0.115926;
      }
      normaliseRms(x, A / Math.SQRT2);
      break;
    }
    case 'sweep-lin':
    case 'sweep-log': {
      const f0 = p.f0 ?? 20;
      const f1 = p.f1 ?? 20000;
      const T = Math.max(p.duration, 1e-9);
      const ratio = f1 / f0;
      const lnR = Math.log(ratio);
      for (let i = 0; i < n; i++) {
        const t = i / fs;
        const cycles =
          p.type === 'sweep-lin'
            ? f0 * t + ((f1 - f0) * t * t) / (2 * T)
            : Math.abs(lnR) < 1e-12
              ? f0 * t
              : ((f0 * T) / lnR) * (Math.exp((t / T) * lnR) - 1);
        x[i] = A * Math.sin(2 * Math.PI * frac(cycles));
      }
      break;
    }
  }
  return { name: signalName(p), sampleRate: fs, channels: [x] };
}

/** Instantaneous frequency of a sweep at time t (for tests / readouts). */
export function sweepFrequencyAt(p: GeneratorParams, t: number): number {
  const f0 = p.f0 ?? 20;
  const f1 = p.f1 ?? 20000;
  const T = p.duration;
  return p.type === 'sweep-lin' ? f0 + ((f1 - f0) * t) / T : f0 * Math.pow(f1 / f0, t / T);
}
